import Download from '@mui/icons-material/Download'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isBetween from 'dayjs/plugin/isBetween'
import { useState } from 'react'

// Self-contained plugin setup: getCorrectReportDateString uses dayjs.utc() and dayjs().isBetween(),
// so extend here rather than rely on some other module having loaded these plugins first.
dayjs.extend(utc)
dayjs.extend(isBetween)
import { MOMENT_FORMATS, REPORT_DATE_TYPES } from 'src/consumer/constants'
import {
  downloadExcelSheetFromSheetJsWbData,
  isValidInteger,
  isValidNumber,
  getNumberStringWithCommas,
} from 'src/consumer/helpers/utilFunctions'
import { getJsonForExportingExcelData, getFeeMapString } from '../utils'
import ScreenerBreakUpReport from './screener/ScreenerBreakUpReport'
import * as XLSX from 'xlsx'

export function getValidIntegerOrNaForClinicReport(value) {
  return isValidInteger(value) ? getNumberStringWithCommas(value) : 'NA'
}

export function getValidPercentageOrNaForClinicReport(value) {
  return isValidNumber(value) ? `${value}%` : 'NA'
}

function getCorrectReportDateString(momentReportDate, momentReportStartDate, reportType) {
  if (reportType === REPORT_DATE_TYPES.day) {
    if (momentReportDate.isSame(dayjs(), 'day')) {
      return 'Today'
    }
    if (momentReportDate.isSame(dayjs().subtract(1, 'day'), 'day')) {
      return 'Yesterday'
    }
    return dayjs
      .utc(momentReportDate)
      .format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)
  }
  if (reportType === REPORT_DATE_TYPES.week) {
    const startOfThisWeek = dayjs().startOf('week')
    const endOfThisWeek = dayjs().endOf('week')
    const startOfLastWeek = dayjs().subtract(1, 'week').startOf('week')
    const endOfLastWeek = dayjs().subtract(1, 'week').endOf('week')

    if (momentReportDate.isBetween(startOfThisWeek, endOfThisWeek, null, '[]')) {
      return 'This Week'
    }
    if (momentReportDate.isBetween(startOfLastWeek, endOfLastWeek, null, '[]')) {
      return 'Last Week'
    }

    const weekOfMonth = Math.ceil(momentReportDate.date() / 7)
    const monthName = momentReportDate.format('MMMM')
    return `${weekOfMonth}th week of ${monthName}`
  }
  if (reportType === REPORT_DATE_TYPES.month) {
    return dayjs.utc(momentReportDate).format('MMM YYYY')
  }
  if (reportType === REPORT_DATE_TYPES.year) {
    return dayjs.utc(momentReportDate).format('YYYY')
  }
  if (reportType === 'custom') {
    return `${dayjs.utc(momentReportStartDate).format('DD MMM, YYYY')}-${dayjs
      .utc(momentReportDate)
      .format('DD MMM, YYYY')}`
  }
}

function ModernTableSection({ title, data, columns, reportType, allClinicLocations, showFee = false }) {
  const theme = useTheme()

  return (
    <Card
      sx={{
        mb: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
              }}
            >
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    py: 2,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row, rowIdx) => (
              <TableRow
                key={rowIdx}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                  transition: 'background-color 0.2s ease',
                  '&:last-child td': {
                    borderBottom: 'none',
                  },
                }}
              >
                {columns.map((col, colIdx) => (
                  <TableCell
                    key={colIdx}
                    sx={{
                      py: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.875rem',
                    }}
                  >
                    {col.render ? col.render(row) : (row[col.key] ?? 'NA')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}

export default function ModernizedTableComponent({
  entityTitle,
  reports: periodWiseReportsForAnEntity,
  reportType,
  screeners,
  downloadButtonRef,
  allClinicLocations,
  defaultExpanded = false,
  accordionOpenAlways = false,
}) {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(defaultExpanded)

  function downloadExcelSheet() {
    const workSheetsJson = getJsonForExportingExcelData(
      periodWiseReportsForAnEntity,
      screeners,
      (repEndTime, startTime) =>
        getCorrectReportDateString(
          dayjs.utc(repEndTime).local(),
          dayjs.utc(startTime).local(),
          reportType,
        ),
      allClinicLocations,
    )

    const workbook = XLSX.utils.book_new()
    Object.values(workSheetsJson).forEach((wbData) => {
      const worksheet = XLSX.utils.json_to_sheet(wbData?.data)
      XLSX.utils.book_append_sheet(workbook, worksheet, wbData?.wbName)
    })

    downloadExcelSheetFromSheetJsWbData({
      workbookData: workbook,
      titleOfSheet: `${entityTitle} Report`,
    })
  }

  // Prepare consultations data
  const consultationsData = periodWiseReportsForAnEntity?.map((report) => {
    const dateLabel = getCorrectReportDateString(
      dayjs.utc(report?.reportEndTime).local(),
      dayjs.utc(report?.reportStartTime).local(),
      reportType,
    )

    return {
      date: dateLabel,
      total: getValidIntegerOrNaForClinicReport(report?.metrics?.consultations?.totalBookedCount),
      new: getValidIntegerOrNaForClinicReport(report?.metrics?.consultations?.newBookedCount),
      repeat: getValidIntegerOrNaForClinicReport(report?.metrics?.consultations?.repeatBookedCount),
      fee: report?.metrics?.consultations?.currencyToFeeCollectedMap
        ? getFeeMapString({
            currencyToFeeCollectedMap: report?.metrics?.consultations?.currencyToFeeCollectedMap,
          })
        : 'NA',
    }
  })

  const consultationsColumns = [
    { key: 'date', label: 'Report Date' },
    { key: 'total', label: 'Total Consultations' },
    { key: 'new', label: 'New Consultations' },
    { key: 'repeat', label: 'Repeat Consultations' },
    { key: 'fee', label: 'Fee Collected' },
  ]

  // Prepare patient visits data
  const patientVisitsData = periodWiseReportsForAnEntity?.map((report) => {
    const dateLabel = getCorrectReportDateString(
      dayjs.utc(report?.reportEndTime).local(),
      dayjs.utc(report?.reportStartTime).local(),
      reportType,
    )

    return {
      date: dateLabel,
      total: getValidIntegerOrNaForClinicReport(report?.metrics?.patientVisits?.totalBookedCount),
      new: getValidIntegerOrNaForClinicReport(report?.metrics?.patientVisits?.newBookedCount),
      repeat: getValidIntegerOrNaForClinicReport(report?.metrics?.patientVisits?.repeatBookedCount),
    }
  })

  const patientVisitsColumns = [
    { key: 'date', label: 'Report Date' },
    { key: 'total', label: 'Patient Visits' },
    { key: 'new', label: 'New Patient Visits' },
    { key: 'repeat', label: 'Repeat Patient Visits' },
  ]

  // Prepare therapies data
  const therapiesData = periodWiseReportsForAnEntity?.map((report) => {
    const dateLabel = getCorrectReportDateString(
      dayjs.utc(report?.reportEndTime).local(),
      dayjs.utc(report?.reportStartTime).local(),
      reportType,
    )

    return {
      date: dateLabel,
      total: getValidIntegerOrNaForClinicReport(report?.metrics?.therapies?.totalBookedCount),
      new: getValidIntegerOrNaForClinicReport(report?.metrics?.therapies?.newBookedCount),
      existing: getValidIntegerOrNaForClinicReport(report?.metrics?.therapies?.repeatBookedCount),
    }
  })

  const therapiesColumns = [
    { key: 'date', label: 'Report Date' },
    { key: 'total', label: 'Therapies' },
    { key: 'new', label: 'New Therapies' },
    { key: 'existing', label: 'Existing Therapies' },
  ]

  return (
    <Card
      sx={{
        mb: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        background: 'white',
      }}
    >
      <Accordion
        expanded={accordionOpenAlways ? accordionOpenAlways : expanded}
        onChange={(e, newVal) => setExpanded(newVal)}
        sx={{
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            accordionOpenAlways ? null : (
              <ExpandMore sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
            )
          }
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
            px: 3,
            py: 2,
            minHeight: 64,
            '&.Mui-expanded': {
              minHeight: 64,
            },
            '& .MuiAccordionSummary-content': {
              my: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
              {entityTitle}
            </Typography>
            <Tooltip title="Download tables in excel">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  downloadExcelSheet()
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
                ref={(el) =>
                  downloadButtonRef ? (downloadButtonRef.current[entityTitle] = el) : null
                }
              >
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, backgroundColor: '#fafafa' }}>
          {/* Consultations Table */}
          {consultationsData && consultationsData.length > 0 && (
            <ModernTableSection
              title="Consultations"
              data={consultationsData}
              columns={consultationsColumns}
              reportType={reportType}
              allClinicLocations={allClinicLocations}
              showFee={true}
            />
          )}

          {/* Patient Details Table */}
          {patientVisitsData && patientVisitsData.length > 0 && (
            <ModernTableSection
              title="Patient Details"
              data={patientVisitsData}
              columns={patientVisitsColumns}
              reportType={reportType}
              allClinicLocations={allClinicLocations}
            />
          )}

          {/* Pre Screenings */}
          <ScreenerBreakUpReport
            reports={periodWiseReportsForAnEntity}
            screeners={screeners}
            heading="Pre Screenings"
            metricString="preScreenings"
            getCorrectReportDateString={(momentReportDate, momentReportStartDate) =>
              getCorrectReportDateString(momentReportDate, momentReportStartDate, reportType)
            }
          />

          {/* Post Screenings */}
          <ScreenerBreakUpReport
            reports={periodWiseReportsForAnEntity}
            screeners={screeners}
            heading="Post Screenings"
            metricString="postScreenings"
            getCorrectReportDateString={(momentReportDate, momentReportStartDate) =>
              getCorrectReportDateString(momentReportDate, momentReportStartDate, reportType)
            }
          />

          {/* Therapy Details Table */}
          {therapiesData && therapiesData.length > 0 && (
            <ModernTableSection
              title="Therapy Details"
              data={therapiesData}
              columns={therapiesColumns}
              reportType={reportType}
              allClinicLocations={allClinicLocations}
            />
          )}

          {/* Therapy/Screening Details */}
          <ScreenerBreakUpReport
            reports={periodWiseReportsForAnEntity}
            screeners={screeners}
            heading="Therapy/Screening Details"
            metricString="therapyPerScreening"
            getCorrectReportDateString={(momentReportDate, momentReportStartDate) =>
              getCorrectReportDateString(momentReportDate, momentReportStartDate, reportType)
            }
          />
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}

